import { Ban, ShoppingBag, UserPlus } from 'lucide-react';
import { AnimatedIcon } from '@/shared/components/animated-icon';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { useMgmReferral } from '../api/use-mgm-admin';
import { REFERRAL_STATUS, REFERRAL_STEPS, eventLabel, rejectReasonLabel, stepsDone } from '../model/referral-status';
import type { MgmReferralDetail, MgmRole, ReferralStatus } from '../model/types';
import { ReferralStatusBadge, RewardBadge } from './mgm-badges';
import { EVENT_FALLBACK, EVENT_ICON, REFERRAL_ICON, ROLE_ICON, type EventTone } from './status-icons';

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

const CHANNEL: Record<string, string> = { invite: 'Link de indicação', app: 'App', operator: 'Balcão (operador)' };

const TONE: Record<EventTone, string> = {
  info: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  accent: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  success: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  warning: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  danger: 'bg-destructive/15 text-destructive',
  neutral: 'bg-muted text-muted-foreground',
};

/** Um lado da indicação: avatar, nome, identificação e a recompensa dele. */
function PersonCard({ role, detail }: { role: MgmRole; detail: MgmReferralDetail }) {
  const isReferrer = role === 'referrer';
  const person = isReferrer ? detail.referrer : detail.referred;
  const registered = isReferrer || Boolean(detail.referred.userId);
  const reward = isReferrer ? detail.rewards.referrer : detail.rewards.referred;
  const RoleIcon = ROLE_ICON[role];
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-border/60 bg-card p-4">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <RoleIcon className="size-3.5" /> {isReferrer ? 'Quem indicou' : 'Indicado'}
      </p>
      <div className="flex min-w-0 items-center gap-3">
        {registered ? (
          <LeadAvatar
            lead={{
              _id: (isReferrer ? detail.referrer.userId : detail.referred.userId) ?? detail.id,
              profile: { name: person.name || '?', uniqueId: person.uniqueId ?? undefined },
              gender: person.gender ?? undefined,
            }}
            className="size-10"
          />
        ) : (
          <span className="grid size-10 shrink-0 place-items-center rounded-full border-2 border-dashed border-border text-muted-foreground">
            <UserPlus className="size-4" />
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-medium">{person.name || 'Aguardando cadastro'}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">
            {isReferrer ? detail.referrer.code : detail.referred.keyMasked}
          </p>
        </div>
      </div>
      <div className="mt-auto">
        {reward ? <RewardBadge reward={reward} /> : <span className="text-xs text-muted-foreground">Sem recompensa</span>}
      </div>
    </div>
  );
}

/** Trilha Indicada → Cadastrada → Convertida, com a data de cada passo. */
function ProgressTrack({ detail }: { detail: MgmReferralDetail }) {
  const done = detail.status === 'rejected' ? 0 : stepsDone(detail.status);
  const dates: Record<ReferralStatus, string | null> = {
    indicated: detail.indicatedAt,
    registered: detail.registeredAt,
    converted: detail.convertedAt,
    rejected: null,
  };
  return (
    <ol className="grid grid-cols-3 gap-2">
      {REFERRAL_STEPS.map((step, i) => {
        const reached = i < done;
        const Icon = REFERRAL_ICON[step];
        return (
          <li key={step} className="flex min-w-0 flex-col gap-2">
            <div className={cn('h-1.5 rounded-full', reached ? 'bg-primary' : 'bg-foreground/[0.08]')} />
            <div className="flex min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  'grid size-6 shrink-0 place-items-center rounded-full',
                  reached ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                )}
              >
                <Icon className="size-3.5" />
              </span>
              <div className="min-w-0">
                <p className={cn('truncate text-xs font-medium', !reached && 'text-muted-foreground')}>
                  {REFERRAL_STATUS[step].label}
                </p>
                <p className="truncate text-[11px] tabular-nums text-muted-foreground">
                  {formatDate(dates[step]) || '—'}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Linha do tempo (mais recente primeiro). Ícone e linha ficam no FLUXO do
 * layout — nada posicionado para fora do container que rola, então nada é
 * cortado.
 */
function Timeline({ events }: { events: MgmReferralDetail['events'] }) {
  const ordered = [...events].reverse();
  return (
    <ol>
      {ordered.map((ev, i) => {
        const meta = EVENT_ICON[ev.type] ?? EVENT_FALLBACK;
        const last = i === ordered.length - 1;
        return (
          <li key={`${ev.type}-${ev.at}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={cn('grid size-8 shrink-0 place-items-center rounded-full', TONE[meta.tone])}>
                <AnimatedIcon icon={meta.icon} motion="pop" delay={Math.min(i, 8) * 0.06} className="size-4" />
              </span>
              {last ? null : <span className="my-1 w-px flex-1 bg-border" aria-hidden />}
            </div>
            <div className={cn('min-w-0 pt-1', last ? 'pb-0' : 'pb-4')}>
              <p className="text-sm font-medium leading-tight">{eventLabel(ev.type)}</p>
              <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">{formatDateTime(ev.at)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Detalhe de uma indicação num dialog: quem indicou quem, em que pé está e a
 * timeline de eventos gravada pelo backend (`events[]`) — a auditoria.
 */
export function MgmReferralDialog({
  idPassbook,
  referralId,
  onClose,
}: {
  idPassbook: string;
  referralId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useMgmReferral(idPassbook, referralId);

  return (
    <Dialog open={Boolean(referralId)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="gap-5 p-4 sm:max-w-2xl sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Indicação
            {data ? <ReferralStatusBadge status={data.status} rejectReason={data.rejectReason} /> : null}
          </DialogTitle>
          <DialogDescription>
            {data
              ? `Via ${CHANNEL[data.channel] ?? data.channel} · indicada em ${formatDateTime(data.indicatedAt)}`
              : 'Quem indicou quem, em que pé está e o histórico completo.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-36 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
            </div>
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-40 rounded-xl" />
          </div>
        ) : (
          <>
            {data.status === 'rejected' ? (
              <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <AnimatedIcon icon={Ban} motion="shake" className="size-4 text-destructive" wrapperClassName="mt-0.5" />
                <div>
                  <p className="font-medium">Indicação não elegível</p>
                  <p className="text-muted-foreground">{rejectReasonLabel(data.rejectReason)}</p>
                </div>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <PersonCard role="referrer" detail={data} />
              <PersonCard role="referred" detail={data} />
            </div>

            <ProgressTrack detail={data} />

            {data.purchase || (data.purchaseDeadline && data.status === 'registered') ? (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border/60 bg-muted/30 p-3 text-sm">
                {data.purchase ? (
                  <span className="flex items-center gap-2">
                    <ShoppingBag className="size-4 text-emerald-600 dark:text-emerald-400" />
                    1ª compra:{' '}
                    <strong className="tabular-nums">
                      {data.purchase.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </strong>
                    <span className="text-muted-foreground">em {formatDateTime(data.purchase.at)}</span>
                  </span>
                ) : null}
                {data.purchaseDeadline && data.status === 'registered' ? (
                  <span className="text-muted-foreground">
                    Prazo da 1ª compra: <span className="tabular-nums text-foreground">{formatDateTime(data.purchaseDeadline)}</span>
                  </span>
                ) : null}
              </div>
            ) : null}

            <section className="space-y-3">
              <p className="text-sm font-semibold">Histórico</p>
              <Timeline events={data.events} />
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
